export type SiteSection =
  | 'home'
  | 'blog'
  | 'blog_post'
  | 'prodeng'
  | 'prodeng_article'
  | 'projects'
  | 'repos'
  | 'music'
  | 'uses'
  | 'about'
  | 'quotes'
  | 'privacy'
  | 'terms'
  | 'demo'
  | 'other';

export type PageContext = Record<string, string | number | boolean | undefined>;

export function classifyPath(pathname: string): { section: SiteSection; content_type: string } {
  if (pathname === '/') return { section: 'home', content_type: 'landing' };
  if (pathname.startsWith('/blog/posts/')) return { section: 'blog_post', content_type: 'article' };
  if (pathname.startsWith('/blog')) return { section: 'blog', content_type: 'index' };
  if (pathname.startsWith('/prodeng/articles/')) return { section: 'prodeng_article', content_type: 'article' };
  if (pathname.startsWith('/prodeng')) return { section: 'prodeng', content_type: 'index' };
  if (pathname.startsWith('/projects/repos')) return { section: 'repos', content_type: 'index' };
  if (pathname.startsWith('/projects')) return { section: 'projects', content_type: 'index' };
  if (pathname.startsWith('/music')) return { section: 'music', content_type: 'tool' };
  if (pathname.startsWith('/uses')) return { section: 'uses', content_type: 'page' };
  if (pathname.startsWith('/about')) return { section: 'about', content_type: 'page' };
  if (pathname.startsWith('/quotes')) return { section: 'quotes', content_type: 'collection' };
  if (pathname.startsWith('/privacy')) return { section: 'privacy', content_type: 'legal' };
  if (pathname.startsWith('/terms')) return { section: 'terms', content_type: 'legal' };
  if (pathname.startsWith('/demo')) return { section: 'demo', content_type: 'demo' };
  return { section: 'other', content_type: 'page' };
}

function utmParams(): PageContext {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'ref'] as const;
  const out: PageContext = {};
  for (const key of keys) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return out;
}

function connectionType(): string | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const conn = (navigator as Navigator & {
    connection?: { effectiveType?: string };
  }).connection;
  return conn?.effectiveType;
}

export function pageContext(): PageContext {
  if (typeof window === 'undefined') return {};

  const { pathname, search, hash, href } = window.location;
  const { section, content_type } = classifyPath(pathname);
  const theme = localStorage.getItem('theme-mode') || 'unknown';
  const isRetro = localStorage.getItem('theme-retro') === 'true';

  return {
    path: pathname,
    search: search || undefined,
    hash: hash || undefined,
    section,
    content_type,
    theme,
    is_retro: isRetro,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || undefined,
    connection_type: connectionType(),
    prefers_reduced_motion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    is_touch: window.matchMedia('(pointer: coarse)').matches,
    color_scheme: window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    hardware_concurrency: navigator.hardwareConcurrency,
    max_touch_points: navigator.maxTouchPoints,
    is_standalone: window.matchMedia('(display-mode: standalone)').matches,
    is_online: navigator.onLine,
    href,
    ...utmParams(),
  };
}
